import {
  ensureTrackerInitialized,
  getTracker,
} from "@/shared/tracking/analytics-helpers";
import { RIVER_EVENT, trackRiver } from "@/services/river";

import type { HandshakeMetadata } from "./lib/collect-metadata";
import { sendCheckoutUiBeacon } from "./lib/nr-beacon";

export const CHECKOUT_HANDSHAKE_EVENT = "CHECKOUT_HANDSHAKE_COMPLETED";

/**
 * Emits the handshake event to Kafka (via the existing tracker → /api/v1/events),
 * to River (via WFL → /api/v2/events) and to New Relic. IP is attached
 * server-side.
 *
 * New Relic goes out via a keepalive beacon (see ./lib/nr-beacon) rather than
 * the browser agent's `addPageAction`: the agent buffers events and only ships
 * them on a 30s timer or a `visibilitychange:hidden` harvest, neither of which
 * survives the handshake page's immediate redirect (measured ~33% delivery).
 * The keepalive beacon completes across navigation, same as the Kafka POST.
 *
 * River is emitted next, before the Kafka await, so it is not suppressed when
 * the v1 tracker is missing or throws. River carries this event because the
 * backend cannot: the customer is not authenticated yet, so checkout-service
 * never observes it.
 *
 * Resolves true when the Kafka POST succeeds. Never throws — a failed beacon
 * must not strand the customer (callers should still navigate after a timeout).
 */
export async function emitCheckoutHandshake(
  checkoutId: string,
  metadata: HandshakeMetadata,
): Promise<boolean> {
  // Fire NR before awaiting Kafka — the keepalive beacon must go out before the
  // navigation timeout tears down the tab.
  sendCheckoutUiBeacon(CHECKOUT_HANDSHAKE_EVENT, {
    checkout_id: checkoutId,
    order_id: metadata.queryParams.orderId ?? "",
    locale: metadata.locale,
    user_agent: metadata.userAgent,
    referrer: metadata.referrer,
    is_embedded: metadata.isEmbedded,
    is_webview: metadata.isWebView,
  });

  // Forwards the whole query string, same as the legacy event — nothing
  // sensitive is permitted in a checkout URL. Canonical metadata fields win if
  // a param happens to share their name.
  trackRiver({
    event: RIVER_EVENT.checkoutRendered,
    checkoutId,
    context: {
      locale: metadata.locale,
      orderId: metadata.queryParams.orderId,
      extra: metadata.queryParams,
    },
  });

  try {
    ensureTrackerInitialized();
    const tracker = getTracker();
    if (!tracker) {
      return false;
    }
    return await tracker.trackAsync({
      eventType: CHECKOUT_HANDSHAKE_EVENT,
      merchantId: "",
      checkoutId,
      orderId: metadata.queryParams.orderId ?? "",
      customerId: "",
      countryCode: "",
      currencyCode: "",
      eventData: {
        userAgent: metadata.userAgent,
        locale: metadata.locale,
        referrer: metadata.referrer,
        queryParams: metadata.queryParams,
        timestamp: metadata.timestamp,
        isEmbedded: metadata.isEmbedded,
        isWebView: metadata.isWebView,
      },
    });
  } catch {
    // swallow — tracking must not break the flow
    return false;
  }
}
